import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  Activity,
  AlertTriangle,
  Biohazard,
  Bomb,
  BusFront,
  Camera,
  ChartNoAxesCombined,
  CircleDot,
  CircleHelp,
  CloudLightning,
  Droplets,
  Earth,
  Factory,
  Flame,
  Footprints,
  GraduationCap,
  HeartPulse,
  Landmark,
  Leaf,
  MessageSquareText,
  Newspaper,
  Plane,
  PlaneTakeoff,
  Radio,
  Route,
  Satellite,
  Scale,
  Search,
  Shield,
  ShieldAlert,
  Ship,
  Siren,
  Stethoscope,
  Sun,
  Swords,
  Tent,
  TrainFront,
  Truck,
  UtensilsCrossed,
  UtilityPole,
  Wind,
  Zap,
} from "lucide-react";

import {
  normalizeMarkerIconType,
  selectMarkerIcon,
  selectMarkerIconType,
} from "./sourcePresentation";

export const CUSTOM_CANVAS_ICON_TYPES = new Set(["recall", "outbreak"]);

// Inputs that are *expected* to resolve to the generic "point" glyph — used to
// avoid noisy dev warnings for legitimately generic/absent icon names.
const GENERIC_ICON_INPUTS = new Set(["", "point", "unknown", "feed_views"]);

export const isCustomCanvasMapIconType = (iconType) =>
  CUSTOM_CANVAS_ICON_TYPES.has(normalizeIconType(iconType));

// Neutral fallback color for a marker whose target_type has no entry in the
// TARGET_TYPE_CONFIG registry (e.g. non-feed live/infra sources).
export const DEFAULT_EVENT_ICON_COLOR = "#94a3b8"; // slate

// target_type → { color, icon } registry. The single source of truth for event
// color/icon is the backend TARGET_TYPE_CONFIG, delivered to the client via
// getFeedViews. OsintMapHome builds this map from the feed-view options and pushes
// it here via setTargetTypeStyles(); the renderer then colours ANY event by its
// target_type (feed or not), instead of a competing client-invented palette.
let targetTypeStyleRegistry = {};

const normalizeTargetTypeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export const setTargetTypeStyles = (styles) => {
  const next = {};
  if (styles && typeof styles === "object") {
    for (const [targetType, style] of Object.entries(styles)) {
      const key = normalizeTargetTypeKey(targetType);
      if (key && style) next[key] = style;
    }
  }
  targetTypeStyleRegistry = next;
};

export const targetTypeStyle = (targetType) =>
  targetTypeStyleRegistry[normalizeTargetTypeKey(targetType)] || null;

const eventTargetType = (eventOrProperties = {}) => {
  const properties = eventProperties(eventOrProperties);
  const meta = properties.meta || {};
  return (
    properties.target_type ||
    properties.targetType ||
    properties.category ||
    meta.target_type ||
    meta.targetType ||
    meta.original_target_type ||
    ""
  );
};

// Color an event by its target_type from the registry; neutral slate if unknown.
export const eventTargetTypeColor = (eventOrProperties = {}) =>
  targetTypeStyle(eventTargetType(eventOrProperties))?.color ||
  DEFAULT_EVENT_ICON_COLOR;

// Icon name for an event's target_type from the registry (or null if unmapped).
export const eventTargetTypeIcon = (eventOrProperties = {}) =>
  targetTypeStyle(eventTargetType(eventOrProperties))?.icon || null;

const ICON_COMPONENTS = {
  // Existing map semantic icon types.
  alert: AlertTriangle,
  "air-quality": Wind,
  bus: BusFront,
  cyber: ShieldAlert,
  conflict: Swords,
  disaster: Siren,
  energy: Zap,
  environment: Leaf,
  factory: Factory,
  flight: Plane,
  health: Activity,
  maritime: Ship,
  news: Newspaper,
  point: CircleDot,
  "regional-post": MessageSquareText,
  route: Route,
  satellite: Satellite,
  seismic: Activity,
  security: ShieldAlert,
  signal: Radio,
  "space-weather": Sun,
  telecom: Radio,
  train: TrainFront,
  utility: UtilityPole,
  webcam: Camera,
  weather: CloudLightning,
  wildfire: Flame,

  // Raw feed-view Lucide icon names returned by getFeedViews.system_views[].icon.
  // Keep these as kebab-case because that is the API shape and what iconType stores.
  earth: Earth,
  "shield-alert": ShieldAlert,
  "plane-takeoff": PlaneTakeoff,
  plane: Plane,
  "heart-pulse": HeartPulse,
  scale: Scale,
  shield: Shield,
  "cloud-lightning": CloudLightning,
  "chart-no-axes-combined": ChartNoAxesCombined,
  swords: Swords,
  ship: Ship,
  landmark: Landmark,
  truck: Truck,
  "circle-help": CircleHelp,
  search: Search,

  // Feed-view target_type icons referenced by FEED_VIEW_ICON_BY_TARGET_TYPE in
  // FlatMapRenderer. These must exist here or the canvas silently falls back to a
  // plain dot (see MAP_TARGET_TYPE_ICON_NAMES guard below).
  biohazard: Biohazard,
  "utensils-crossed": UtensilsCrossed,
  siren: Siren,
  bomb: Bomb,
  flame: Flame,
  tent: Tent,
  droplets: Droplets,
  footprints: Footprints,
  school: GraduationCap,
  // GDELT GKG taxonomy v2 buckets (2026-08).
  stethoscope: Stethoscope,
  zap: Zap,
};

// Icon names that must resolve to a real Lucide component (not the CircleDot
// fallback). The FlatMapRenderer target_type table is validated against this at
// module load so a missing glyph fails loudly in dev instead of rendering a dot.
export const KNOWN_ICON_TYPES = new Set(Object.keys(ICON_COMPONENTS));

export const isKnownIconType = (iconType) =>
  KNOWN_ICON_TYPES.has(String(iconType || "").trim().toLowerCase());

export const normalizeIconType = (value) => {
  const normalized = normalizeMarkerIconType(value);
  if (!normalized) return "point";
  if (normalized === "recalls") return "recall";
  if (normalized === "outbreaks" || normalized === "disease") return "outbreak";
  if (CUSTOM_CANVAS_ICON_TYPES.has(normalized)) return normalized;
  return ICON_COMPONENTS[normalized] ? normalized : "point";
};

const eventProperties = (eventOrProperties = {}) =>
  eventOrProperties?.properties || eventOrProperties || {};

const explicitIconType = (properties = {}) =>
  normalizeIconType(
    properties.feedViewIcon ||
      properties.iconType ||
      properties.groupIconType ||
      properties.icon ||
      properties.markerIcon,
  );

export const getMapEventIconType = (eventOrProperties = {}) => {
  const properties = eventProperties(eventOrProperties);

  // Feed map points already know their icon via getFeedViews.icon. Do not let the
  // older sourcePresentation semantic layer collapse them into recall/outbreak/point.
  if (properties.source === "feed_views") {
    const feedIconType = explicitIconType(properties);
    if (feedIconType !== "point") return feedIconType;
  }

  const presentationType = normalizeIconType(selectMarkerIconType(properties));
  if (presentationType !== "point") return presentationType;

  const explicitType = explicitIconType(properties);
  if (explicitType !== "point") return explicitType;

  return "point";
};

export const getMapEventIconComponent = (eventOrProperties = {}) => {
  const iconType = getMapEventIconType(eventOrProperties);
  if (CUSTOM_CANVAS_ICON_TYPES.has(iconType)) return null;

  const properties = eventProperties(eventOrProperties);
  return ICON_COMPONENTS[iconType] || selectMarkerIcon(properties) || CircleDot;
};

const svgCache = new Map();

export const getMapEventIconSvgDataUrl = (iconType, color = "#38bdf8") => {
  const normalizedType = normalizeIconType(iconType);

  // Important: recall/outbreak are custom canvas drawings in FlatMapRenderer.
  // Returning null here prevents the Lucide SVG path from ever winning for them.
  if (CUSTOM_CANVAS_ICON_TYPES.has(normalizedType)) return null;

  // Fail loudly (dev only): a caller asked for a real icon name that isn't in the
  // table, so normalizeIconType downgraded it to "point". This is exactly the class
  // of bug where markers silently render as generic dots — surface it here.
  if (
    process.env.NODE_ENV !== "production" &&
    normalizedType === "point" &&
    !GENERIC_ICON_INPUTS.has(String(iconType || "").trim().toLowerCase())
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[MapEventIcons] Unknown icon type "${iconType}" fell back to a plain dot. ` +
        "Add it to ICON_COMPONENTS or map its target_type to an existing icon.",
    );
  }

  const Icon = ICON_COMPONENTS[normalizedType] || CircleDot;
  const safeColor = String(color || DEFAULT_EVENT_ICON_COLOR);
  const cacheKey = `${normalizedType}:${safeColor}`;

  if (svgCache.has(cacheKey)) return svgCache.get(cacheKey);

  // Render at a larger intrinsic size than the ~12–17px the marker draws at, so the
  // browser rasterizes the SVG at high resolution and the canvas downscales it (I6:
  // avoids the soft look of upscaling a 24px raster on HiDPI displays). strokeWidth
  // is in the 24-unit viewBox, so it stays proportional at any render size.
  const markup = renderToStaticMarkup(
    <Icon
      aria-hidden="true"
      color={safeColor}
      fill="none"
      size={48}
      strokeWidth={2.25}
    />,
  );
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  svgCache.set(cacheKey, dataUrl);
  return dataUrl;
};
